import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StatCardProps {
  label: string;
  value: string;
  hint?: string;
  icon?: LucideIcon;
  /** Draws attention when the number is something to act on (open issues, stuck cases). */
  tone?: 'neutral' | 'warning';
  onClick?: () => void;
  className?: string;
}

export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = 'neutral',
  onClick,
  className,
}: StatCardProps) {
  const interactive = Boolean(onClick);
  const Wrapper = interactive ? 'button' : 'div';

  return (
    <Wrapper
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex flex-col gap-1 rounded-2xl border p-4 text-left transition-colors',
        tone === 'warning'
          ? 'border-amber-500/35 bg-amber-500/10'
          : 'border-content-highlight/35 bg-content-dark/45',
        interactive && 'hover:border-primary/45 hover:bg-primary/10',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">{label}</p>
        {Icon ? <Icon className={cn('h-4 w-4', tone === 'warning' ? 'text-amber-300' : 'text-white/35')} /> : null}
      </div>
      <p className="text-2xl font-semibold tracking-tight text-white">{value}</p>
      {hint ? <p className="text-xs text-white/50">{hint}</p> : null}
    </Wrapper>
  );
}
